/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-misused-promises */

import { BASE_SERVICE_URI } from '../../constants/ApiConstants';
import dataFetchingClient from '../../utils/DataFetchingClient';
import { registerMutation, registerQuery, useMutation, useQuery } from 'academy-common-lib/utils/DataFetchingClient';
import http from 'hub-http/clients/apiClient';
import { useCallback } from 'react';
import Raven from 'raven-js';
import { getQueryFieldName } from '../../utils/DataFetchingClientCache';
import { TABLE_SIZE } from '../table';
export const ASSIGNMENTS_PROGRESS_API = `${BASE_SERVICE_URI}/progress/v1/team/playlist-assignments/`;
export const ASSIGNMENTS_API = `${BASE_SERVICE_URI}/assignments/v1/playlists/`;
export const ASSIGNMENTS_USERS_API = `${BASE_SERVICE_URI}/assignments/v1/users/`;
const GET_PLAYLIST_ASSIGNMENTS = registerQuery({
  fieldName: 'playlistAssignments',
  args: ['assignmentIds'],
  fetcher: ({
    assignmentIds
  }) => http.get(`${ASSIGNMENTS_API}batch/ids/${assignmentIds.join(',')}`)
});
export const usePlaylistAssignments = ({
  playlistId,
  portalId,
  assignmentIds,
  skip
}) => {
  const {
    data,
    loading,
    error
  } = useQuery(GET_PLAYLIST_ASSIGNMENTS, {
    skip,
    variables: {
      playlistId,
      portalId,
      assignmentIds
    }
  });
  return {
    playlistAssignments: data,
    playlistAssignmentsLoading: loading,
    playlistAssignmentsError: error
  };
};
const GET_PLAYLIST_ASSIGNMENT_PORTALS = registerQuery({
  fieldName: 'playlistAssignmentsPortals',
  args: ['playlistId', 'offset', 'limit', 'query', 'assignedByRequestingUser'],
  fetcher: ({
    playlistId,
    offset,
    limit,
    query,
    assignedByRequestingUser
  }) => http.get(`${ASSIGNMENTS_API}${playlistId}/aggregation`, {
    query: {
      offset,
      limit,
      query,
      assignedByRequestingUser
    }
  })
});
export const usePlaylistAssignmentsPortals = ({
  playlistId,
  offset = 0,
  limit = TABLE_SIZE,
  skip = false,
  query,
  assignedByRequestingUser = false
}) => {
  const {
    data,
    loading,
    error
  } = useQuery(GET_PLAYLIST_ASSIGNMENT_PORTALS, {
    skip,
    variables: {
      playlistId,
      offset,
      limit,
      query,
      assignedByRequestingUser
    }
  });
  return {
    total: data ? data.total : 0,
    playlistAssignmentsPortals: data ? data.results : null,
    playlistAssignmentsPortalsLoading: loading,
    playlistAssignmentsPortalsError: error
  };
};
const GET_PLAYLIST_ASSIGNMENTS_FOR_PORTAL = registerQuery({
  fieldName: 'playlistAssignments',
  args: ['playlistId', 'offset', 'limit', 'portalId', 'query', 'assignedByRequestingUser'],
  fetcher: ({
    playlistId,
    offset,
    limit,
    portalId,
    query,
    assignedByRequestingUser
  }) => {
    return http.get(`${ASSIGNMENTS_API}${playlistId}/portal/${portalId}`, {
      query: {
        offset,
        limit,
        query,
        assignedByRequestingUser
      }
    });
  }
});
export const usePlaylistAssignmentsForPortal = ({
  playlistId,
  portalId,
  offset = 0,
  limit = TABLE_SIZE,
  skip = false,
  query,
  assignedByRequestingUser = false
}) => {
  const {
    data,
    loading,
    error
  } = useQuery(GET_PLAYLIST_ASSIGNMENTS_FOR_PORTAL, {
    skip,
    variables: {
      playlistId,
      portalId,
      offset,
      limit,
      query,
      assignedByRequestingUser
    }
  });
  return {
    total: data ? data.total : 0,
    playlistAssignmentsForPortal: data ? data.results : null,
    playlistAssignmentsForPortalLoading: loading,
    playlistAssignmentsForPortalError: error
  };
};
export const refetchAssignments = () => dataFetchingClient.refetchQueries({
  updateCache(cache) {
    cache.evict({
      fieldName: getQueryFieldName(GET_PLAYLIST_ASSIGNMENT_PORTALS)
    });
    cache.evict({
      fieldName: getQueryFieldName(GET_PLAYLIST_ASSIGNMENTS_FOR_PORTAL)
    });
  }
});
const CREATE_PLAYLIST_ASSIGNMENT = registerMutation({
  fieldName: 'createPlaylistAssignment',
  args: ['playlistId', 'portalId', 'assigneeUserIds', 'dueDate'],
  fetcher: data => http.post(ASSIGNMENTS_API, {
    data
  })
});
export const useCreatePlaylistAssignment = ({
  onCompleted
} = {}) => {
  const [mutate, {
    loading: createPlaylistAssignmentLoading,
    error: createPlaylistAssignmentError
  }] = useMutation(CREATE_PLAYLIST_ASSIGNMENT, {
    update: async (cache, {
      errors
    }) => {
      if (errors && errors.length) return;
      await refetchAssignments();
    },
    onCompleted: assignmentsRecord => {
      if (onCompleted) onCompleted(assignmentsRecord.createPlaylistAssignment);
    }
  });
  const createPlaylistAssignment = useCallback(variables => {
    return mutate({
      variables
    }).then(response => {
      if (response && response.data) {
        const {
          createPlaylistAssignment: createResponse
        } = response.data;
        if (createResponse) {
          return createResponse;
        }
        return null;
      }
      return null;
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [mutate]);
  return {
    createPlaylistAssignment,
    createPlaylistAssignmentLoading,
    createPlaylistAssignmentError
  };
};
const UPDATE_PLAYLIST_ASSIGNMENTS = registerMutation({
  fieldName: 'updatePlaylistAssignments',
  args: ['assignmentIds', 'dueDate'],
  fetcher: ({
    assignmentIds,
    dueDate
  }) => http.patch(`${ASSIGNMENTS_API}batch/${assignmentIds.join(',')}`, {
    data: {
      dueDate
    }
  })
});
export const useUpdatePlaylistAssignments = ({
  onCompleted
} = {}) => {
  const [mutate, {
    loading: updatePlaylistAssignmentsLoading,
    error: updatePlaylistAssignmentsError
  }] = useMutation(UPDATE_PLAYLIST_ASSIGNMENTS, {
    update: async (cache, {
      errors
    }) => {
      if (errors && errors.length) return;
      await refetchAssignments();
    },
    onCompleted: assignmentsRecord => {
      if (onCompleted) onCompleted(assignmentsRecord.updatePlaylistAssignments);
    }
  });
  const updatePlaylistAssignments = useCallback(variables => {
    return mutate({
      variables
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [mutate]);
  return {
    updatePlaylistAssignments,
    updatePlaylistAssignmentsLoading,
    updatePlaylistAssignmentsError
  };
};
const DELETE_PLAYLIST_ASSIGNMENT = registerMutation({
  fieldName: 'deletePlaylistAssignment',
  args: ['assignmentId'],
  fetcher: ({
    assignmentId
  }) => http.delete(`${ASSIGNMENTS_API}${assignmentId}`)
});
export const useDeletePlaylistAssignment = ({
  onCompleted
} = {}) => {
  const [mutate, {
    loading: deletePlaylistAssignmentLoading,
    error: deletePlaylistAssignmentError
  }] = useMutation(DELETE_PLAYLIST_ASSIGNMENT, {
    onCompleted: () => {
      if (onCompleted) onCompleted();
      // TODO: refetch get assignments once Hektor updates endpoint to return deleted item info
    }
  });
  const deletePlaylistAssignment = useCallback(variables => {
    return mutate({
      variables
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [mutate]);
  return {
    deletePlaylistAssignment,
    deletePlaylistAssignmentLoading,
    deletePlaylistAssignmentError
  };
};
const HUBSPOTTER_GET_USERS_FOR_ACCOUNT = registerQuery({
  fieldName: 'hubSpotterUsersForAccount',
  args: ['accountId', 'query', 'offset'],
  fetcher({
    accountId,
    query,
    offset
  }) {
    return http.get(`${ASSIGNMENTS_USERS_API}portal/${accountId}/search`, {
      query: {
        query,
        offset
      }
    });
  }
});
export const useHubSpotterUsersForAccount = ({
  accountId
}) => {
  const {
    data: usersForAccount,
    loading: usersForAccountLoading,
    error: usersForAccountError,
    refetch
  } = useQuery(HUBSPOTTER_GET_USERS_FOR_ACCOUNT, {
    variables: {
      accountId
    }
  });
  const refetchUsersForAccount = useCallback(async ({
    query,
    offset
  }) => {
    const results = await refetch({
      accountId,
      query,
      offset
    });
    return results.data.hubSpotterUsersForAccount;
  }, [accountId, refetch]);
  return {
    usersForAccount,
    usersForAccountLoading,
    usersForAccountError,
    refetchUsersForAccount
  };
};